import type { Agent } from './agent';
import type { RunContext } from './run-context';
import type { UnknownContext } from './types';
import type * as protocol from './types/protocol';

export interface ToolGuardrailBase {
  name: string;
}

/**
 * 工具护栏在评估后应采取的操作。
 *
 * - `allow`：继续执行下一个护栏或工具执行/输出处理。
 * - `rejectContent`：将该护栏视为拒绝调用/输出，并通过一条消息进行短路处理。
 * - `throwException`：立即升级为触发式异常，以快速失败并暴露诊断信息。
 */
export type ToolGuardrailBehavior =
  | { type: 'allow' }
  | { type: 'rejectContent'; message: string }
  | { type: 'throwException' };

/**
 * Input data passed to a tool input guardrail function.
 */
export interface ToolInputGuardrailData<TContext = UnknownContext> {
  context: RunContext<TContext>;
  agent: Agent<any, any>;
  toolCall: protocol.FunctionCallItem;
}

export interface ToolOutputGuardrailData<TContext = UnknownContext>
  extends ToolInputGuardrailData<TContext> {
  output: unknown;
}

/**
 * The output of a tool guardrail function.
 *
 * `behavior` drives runner control flow; `outputInfo` is optional, structured metadata for tracing or debugging.
 */
export interface ToolGuardrailFunctionOutput {
  /**
   * Additional data about the guardrail evaluation.
   */
  outputInfo?: any;
  /**
   * The behavior the runner should take in response to this guardrail.
   */
  behavior: ToolGuardrailBehavior;
}

export type ToolInputGuardrailFunction<TContext = UnknownContext> = (
  data: ToolInputGuardrailData<TContext>,
) => Promise<ToolGuardrailFunctionOutput>;

export interface ToolInputGuardrailDefinition<T = UnknownContext>
  extends ToolGuardrailBase {
  type: 'tool_input';
  run: ToolInputGuardrailFunction<T>;
}

export type ToolOutputGuardrailFunction<TContext = UnknownContext> = (
  data: ToolOutputGuardrailData<TContext>,
) => Promise<ToolGuardrailFunctionOutput>;

export interface ToolOutputGuardrailDefinition<TContext = UnknownContext>
  extends ToolGuardrailBase {
  type: 'tool_output';
  run: ToolOutputGuardrailFunction<TContext>;
}

export interface ToolGuardrailMetadata {
  type: 'tool_input' | 'tool_output';
  name: string;
}

export interface ToolInputGuardrailResult {
  guardrail: ToolGuardrailMetadata & { type: 'tool_input' };
  output: ToolGuardrailFunctionOutput;
}

export interface ToolOutputGuardrailResult {
  guardrail: ToolGuardrailMetadata & { type: 'tool_output' };
  output: ToolGuardrailFunctionOutput;
}

type ToolInputGuardrailInit<TContext = UnknownContext> =
  | ToolInputGuardrailDefinition<TContext>
  | {
      name: string;
      run: ToolInputGuardrailFunction<TContext>;
    };

type ToolOutputGuardrailInit<TContext = UnknownContext> =
  | ToolOutputGuardrailDefinition<TContext>
  | {
      name: string;
      run: ToolOutputGuardrailFunction<TContext>;
    };

export function resolveToolInputGuardrails<TContext = UnknownContext>(
  guardrails?: ToolInputGuardrailInit<TContext>[],
): ToolInputGuardrailDefinition<TContext>[] {
  if (!guardrails) {
    return [];
  }
  return guardrails.map((gr) =>
    'type' in gr && gr.type === 'tool_input'
      ? (gr as ToolInputGuardrailDefinition<TContext>)
      : defineToolInputGuardrail(gr as { name: string; run: any }),
  );
}

export function resolveToolOutputGuardrails<TContext = UnknownContext>(
  guardrails?: ToolOutputGuardrailInit<TContext>[],
): ToolOutputGuardrailDefinition<TContext>[] {
  if (!guardrails) {
    return [];
  }
  return guardrails.map((gr) =>
    'type' in gr && gr.type === 'tool_output'
      ? (gr as ToolOutputGuardrailDefinition<TContext>)
      : defineToolOutputGuardrail(gr as { name: string; run: any }),
  );
}

export function defineToolInputGuardrail<TContext = UnknownContext>(args: {
  name: string;
  run: ToolInputGuardrailFunction<TContext>;
}): ToolInputGuardrailDefinition<TContext> {
  return {
    type: 'tool_input',
    name: args.name,
    run: args.run,
  };
}

export function defineToolOutputGuardrail<TContext = UnknownContext>(args: {
  name: string;
  run: ToolOutputGuardrailFunction<TContext>;
}): ToolOutputGuardrailDefinition<TContext> {
  return {
    type: 'tool_output',
    name: args.name,
    run: args.run,
  };
}

export const ToolGuardrailFunctionOutputFactory = {
  allow(outputInfo?: any): ToolGuardrailFunctionOutput {
    return {
      behavior: { type: 'allow' },
      outputInfo,
    };
  },
  rejectContent(
    message: string,
    outputInfo?: any,
  ): ToolGuardrailFunctionOutput {
    return {
      behavior: { type: 'rejectContent', message },
      outputInfo,
    };
  },
  throwException(outputInfo?: any): ToolGuardrailFunctionOutput {
    return {
      behavior: { type: 'throwException' },
      outputInfo,
    };
  },
};
